import { Component, DestroyRef, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { distinctUntilChanged, filter, mergeMap, switchMap, tap } from "rxjs/operators";
import { EMPTY, Observable } from "rxjs";
import { ToastrService } from "ngx-toastr";

import { BehaviorScreen } from "../../core/models/view-models/monitor-models/screen.type";
import { ViewMode } from "src/app/core/enums/view-mode.enum";
import { DisplayAccountViewModel } from "../../core/models/view-models/account-models/user-models/display-account-view-model.type";
import { ToastrMessageEnum } from "../../core/enums/toastr-messages/toastr-message.enum";
import { toBooleanProperty } from "../../helpers/type-coercion";
import { UpdateMonitorImageResolutionViewModel } from "../../core/models/view-models/monitor-models/update-monitor-image-resolution.view-model";
import { FullScreenMode } from "../../core/models/view-models/monitor-models/full-screen-mode.model";
import { AppConstants } from "../../app.constants";
import { Monitor } from "../../core/models/entities/monitor.type";
import { ScreenShareRouteParams } from "../../core/interfaces/screen-share-route-params.interface";

import { ScreensStateService } from "../../core/services/screen-state/screens-state.service";
import { MonitorChangeViewService } from "../../core/services/http-services/monitor-change-view.service";
import { AuthService } from "../../core/services/auth/auth.service";
import { MonitorHub } from "../../shared-module/hubs/monitor.hub";
import { MonitorsService } from "../../core/services/http-services/monitors.service";

@Component({
  selector: 'full-screen',
  templateUrl: './full-screen.component.html',
  styleUrls: ['./full-screen.component.scss']
})
export class FullScreenComponent implements OnInit, OnDestroy {
  @ViewChild('fullScreenContainer') fullScreenContainer: ElementRef;

  routeParams: ScreenShareRouteParams;
  screen: BehaviorScreen;
  viewMode = ViewMode.FullScreen;
  user: DisplayAccountViewModel;
  isZoomIn: boolean;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private screenStateService: ScreensStateService,
    private authService: AuthService,
    private monitorChangeViewService: MonitorChangeViewService,
    private toastrService: ToastrService,
    private destroyRef: DestroyRef,
    private monitorHub: MonitorHub,
    private monitorsService: MonitorsService
  ) {}

  ngOnInit(): void {
    this.getRouteParams();
    this.authService.userId ? this.initializeAuthenticatedConnection() : this.initializeAnonymousConnection();

    this.monitorHub.resolutionChanged.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.setScreenResolution());
  }

  ngOnDestroy(): void {
    if (this.screen) {
      this.removeMonitorFromFullScreenMode();
      this.screen.disconnect();
    }

    if (this.monitorHub && this.monitorHub.Connected) {
      this.monitorHub.disconnect();
    }
  }

  zoom(event: MouseEvent) {
    this.isZoomIn = !this.isZoomIn;

    const width = window.innerWidth
      || document.documentElement.clientWidth
      || document.body.clientWidth;

    const height = window.innerHeight
      || document.documentElement.clientHeight
      || document.body.clientHeight;

    if (this.isZoomIn) {
      this.fullScreenContainer.nativeElement.classList.add('zoom-in');
      window.scrollTo(event.offsetX - (width / 15), event.offsetY - (height / 15));
    }
    else {
      this.fullScreenContainer.nativeElement.classList.remove('zoom-in');
    }
  }

  private initializeAuthenticatedConnection(): void {
    this.getUserData();
    this.screenStateService.initMonitors();
    this.subscribeToScreenCollectionChanges();
  }

  private initializeAnonymousConnection(): void {
    if (this.routeParams.physicallAddress) {
      this.createMonitor();
    }
    else {
      this.handleMonitorNotFound();
    }
  }

  private subscribeToScreenCollectionChanges() {
    this.screenStateService.screensCollectionChanged.pipe(
      filter(screens => screens?.length > 0),
      distinctUntilChanged(),
      mergeMap(screens => this.handleMonitorSelection(screens)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  private createMonitor() {
    const monitor = new Monitor();
    monitor.MachinePhysicalAddress = this.routeParams.physicallAddress;
    monitor.MonitorHandler = this.isWindowMode() ? this.routeParams.windowHandle : this.routeParams.monitorHandler;

    this.monitorsService.getAnonymousParameter(monitor.MachinePhysicalAddress, monitor.MonitorHandler, true).pipe(
      switchMap(isAllowed => {
        if (isAllowed) {
          this.screen = this.screenStateService.createBehaviorScreen(monitor);
          return this.configureFullScreenMode(this.isWindowMode());
        }

        this.handleMonitorNotFound();
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  private setScreenResolution() {
    const [width, height] = this.routeParams.resolution.split('x');
    const imageModel = new UpdateMonitorImageResolutionViewModel(this.screen, Number(height), Number(width));

    this.monitorChangeViewService.changeImageResolution(imageModel, false, !this.authService.userId).subscribe();
  }

  private handleMonitorSelection(screens: BehaviorScreen[]): Observable<void> {
    this.screen = screens.find(screen => screen.MonitorHandler === this.routeParams.monitorHandler);

    if (!this.screen) {
      this.handleMonitorNotFound();
      return EMPTY;
    }

    this.screen.MonitorHandler = this.isWindowMode() ? this.routeParams.windowHandle : this.screen.MonitorHandler;
    return this.configureFullScreenMode(this.isWindowMode());
  }

  configureFullScreenMode(isWindow: boolean): Observable<void> {
    const isAnonymous = !this.authService.userId;
    this.screen.connect(isAnonymous);

    const fullScreenModel = new FullScreenMode(
      this.screen.MachinePhysicalAddress,
      this.screen.MonitorHandler,
      true,
      this.routeParams.refreshRate
    );

    if (isWindow) {
      [fullScreenModel.Width, fullScreenModel.Height] = this.routeParams.resolution
        .split('x')
        .map(Number);
    }
    else {
      this.setScreenResolution();
    }

    return this.monitorChangeViewService.setFullScreenMode(fullScreenModel, isAnonymous);
  }

  private getUserData() {
    this.authService.getCaller().pipe(
      tap(user => {
        const isScreenAvailable = user.AvailableMonitors.some(mon => mon.MonitorHandler === this.routeParams.monitorHandler);

        if (!isScreenAvailable) {
          this.handleMonitorNotFound();
        }
      })
    ).subscribe();
  }

  private handleMonitorNotFound() {
    this.router.navigateByUrl('/');
    this.toastrService.error(
      ToastrMessageEnum.PermissionDeniedMessage,
      ToastrMessageEnum.PermissionDeniedTitle,
      {
        toastClass: 'client-monitor-error-toastr'
      }
    );
  }

   private getRouteParams() {
    const [monitorHandler, physicallAddress] = this.getParam(AppConstants.FULL_SCREEN_ROUTE_PARAMS.ID).split(AppConstants.FULL_MONITOR_KEY_DELIMITER);

    this.routeParams = {
      monitorHandler,
      physicallAddress,
      windowHandle: this.getParam(AppConstants.FULL_SCREEN_ROUTE_PARAMS.HANDLE),
      resolution: this.getParam(AppConstants.FULL_SCREEN_ROUTE_PARAMS.RESOLUTION),
      refreshRate: Number(this.getParam(AppConstants.FULL_SCREEN_ROUTE_PARAMS.REFRESH_RATE)),
      showLabel: toBooleanProperty(this.getParam(AppConstants.FULL_SCREEN_ROUTE_PARAMS.LABLE))
    }
  }

  private getParam(key: string): string | null {
    return this.route.snapshot.paramMap.get(key);
  }

  private isWindowMode(): boolean {
    return +this.routeParams.windowHandle !== AppConstants.FULL_SCREEN_DEFAULT_HANDLE;
  }

  @HostListener('window:beforeunload')
  removeMonitorFromFullScreenMode() {
    this.screen?.disableFullScreenMode();
  }
}
